/**
 * HintRevealOverlay — progressive hint system overlay (P16.2).
 *
 * Shows the question and a "Reveal Hint" button. Each click reveals
 * the next hint cumulatively. After all hints are shown, the button
 * transitions to "Reveal Answer". Once the answer is visible, a
 * "Got it" button closes the overlay and emits the telemetry event.
 *
 * Props:
 *   marker   — { question, hints: string[], answer }
 *   sceneId  — string scene ID for event context
 *   onClose  — () => void called to dismiss
 */

import React, { useState } from 'react';
import { emitLearnerEvent } from '../../services/learnerEvents';

export default function HintRevealOverlay({ marker, sceneId, onClose }) {
  const [hintsShown, setHintsShown] = useState(0);
  const [answerVisible, setAnswerVisible] = useState(false);
  const hints = marker?.hints || [];
  const allHintsRevealed = hintsShown >= hints.length;

  const handleRevealNext = () => {
    if (!allHintsRevealed) {
      setHintsShown((n) => n + 1);
    } else {
      setAnswerVisible(true);
    }
  };

  const handleGotIt = async () => {
    await emitLearnerEvent({
      event_type: 'overlay_answered',
      overlay_type: 'hint_reveal',
      scene_id: sceneId,
      hints_used: hintsShown,
      viewed_answer: answerVisible,
    });
    if (onClose) onClose();
  };

  const handleSkip = async () => {
    await emitLearnerEvent({
      event_type: 'overlay_skipped',
      overlay_type: 'hint_reveal',
      scene_id: sceneId,
      hints_used: hintsShown,
      viewed_answer: answerVisible,
    });
    if (onClose) onClose();
  };

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          <span style={labelStyle}>Hint Reveal</span>
          {!answerVisible && (
            <button onClick={handleSkip} style={skipBtnStyle}>
              Skip
            </button>
          )}
        </div>

        <p style={questionStyle}>{marker?.question}</p>

        {hintsShown > 0 && (
          <div style={hintsContainerStyle}>
            {hints.slice(0, hintsShown).map((hint, idx) => (
              <div key={idx} style={hintItemStyle}>
                <div style={hintNumberStyle}>Hint {idx + 1}</div>
                <p style={{ margin: 0, color: '#e2e8f0', fontSize: 14, lineHeight: 1.6 }}>
                  {hint}
                </p>
              </div>
            ))}
          </div>
        )}

        {answerVisible && (
          <div style={answerDisplayStyle}>
            <div style={subLabelStyle}>Answer</div>
            <p style={{ margin: 0, color: '#86efac', fontSize: 14, lineHeight: 1.6 }}>
              {marker?.answer}
            </p>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          {!answerVisible ? (
            <button onClick={handleRevealNext} style={primaryBtnStyle}>
              {allHintsRevealed
                ? 'Reveal Answer'
                : `Reveal Hint ${hintsShown + 1} of ${hints.length}`}
            </button>
          ) : (
            <button onClick={handleGotIt} style={gotItBtnStyle}>
              Got it
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(0,0,0,0.85)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
  backdropFilter: 'blur(4px)',
};

const cardStyle = {
  background: '#1e293b',
  borderRadius: 12,
  padding: '24px 28px',
  maxWidth: 560,
  width: '90%',
  border: '1px solid #334155',
  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
  maxHeight: '80vh',
  overflowY: 'auto',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 14,
};

const labelStyle = {
  fontSize: 11,
  letterSpacing: 1.5,
  textTransform: 'uppercase',
  color: '#94a3b8',
};

const subLabelStyle = {
  fontSize: 10,
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  color: '#64748b',
  marginBottom: 6,
};

const skipBtnStyle = {
  background: 'none',
  border: '1px solid #334155',
  borderRadius: 4,
  color: '#64748b',
  fontSize: 11,
  cursor: 'pointer',
  padding: '2px 8px',
};

const questionStyle = {
  fontSize: 17,
  fontWeight: 600,
  color: '#f8fafc',
  margin: '0 0 16px',
  lineHeight: 1.5,
};

const hintsContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  marginBottom: 12,
};

const hintItemStyle = {
  background: '#0f172a',
  border: '1px solid #334155',
  borderRadius: 8,
  padding: '12px 14px',
};

const hintNumberStyle = {
  fontSize: 10,
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  color: '#f59e0b',
  marginBottom: 6,
  fontWeight: 700,
};

const answerDisplayStyle = {
  background: '#052e16',
  border: '1px solid #166534',
  borderRadius: 8,
  padding: '12px 14px',
  marginBottom: 4,
};

const primaryBtnStyle = {
  flex: 1,
  padding: '10px 0',
  background: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const gotItBtnStyle = {
  ...primaryBtnStyle,
  background: '#059669',
};
